import { Platform } from 'react-native';

/**
 * Global hover affordance for the WEB app (Wave 63, user ask): every
 * tappable thing should look tappable on pointer devices.
 *
 * Injected at runtime because `web.output: "single"` ships a bare SPA
 * shell — +html.tsx is only honored by static output, so a <style> in
 * there never reaches production.
 *
 * RN-web renders Pressables as focusable divs (tabindex="0", plus
 * role="button" when declared), so one CSS layer covers the whole
 * app: pointer cursor + a gentle brightness dip with a short
 * transition. Scoped to hover-capable fine pointers so touch devices
 * never pay for it; components with bespoke hover states (rows, FAB,
 * tiles) stack on this baseline harmlessly.
 */
const HOVER_CSS = `
/* Styled scrollbars everywhere (Wave 68, user call: default bars are
   ugly; any visible bar must match the app). Thin rounded ink-toned
   thumb on a transparent track — quiet until you need it. Firefox
   gets the two-value shorthand. */
* {
  scrollbar-width: thin;
  scrollbar-color: rgba(119,110,95,0.35) transparent;
}
*::-webkit-scrollbar { width: 8px; height: 8px; }
*::-webkit-scrollbar-track { background: transparent; }
*::-webkit-scrollbar-thumb {
  background: rgba(119,110,95,0.32);
  border-radius: 999px;
  border: 2px solid transparent;
  background-clip: padding-box;
}
*::-webkit-scrollbar-thumb:hover { background: rgba(119,110,95,0.55); background-clip: padding-box; border: 2px solid transparent; }
*::-webkit-scrollbar-corner { background: transparent; }

@media (hover: hover) and (pointer: fine) {
  [role="button"], div[tabindex="0"] {
    cursor: pointer;
    transition: filter 140ms ease-out;
  }
  [role="button"]:hover, div[tabindex="0"]:hover {
    filter: brightness(0.96);
  }
  input, textarea, input:hover, textarea:hover { filter: none; }
  input, textarea { cursor: text; }
}
`;

let injected = false;

export function ensureWebHoverCss(): void {
  if (Platform.OS !== 'web' || injected) return;
  if (typeof document === 'undefined') return;
  injected = true;
  const style = document.createElement('style');
  style.setAttribute('data-homestead', 'hover-affordance');
  style.textContent = HOVER_CSS;
  document.head.appendChild(style);
}
